"use client";

import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import classNames from "classnames";
import { isSafari } from "helpers/browser";
import { throttle } from "lodash";
import * as React from "react";
import { useState } from "react";

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    contentClassName?: string;
    hideScrollBar?: boolean;
    orientation?: "vertical" | "horizontal";
    scrollBarClassName?: string;
    scrollScrollBorder?: boolean;
  }
>(
  (
    {
      className,
      children,
      contentClassName,
      hideScrollBar = false,
      orientation = "vertical",
      scrollBarClassName,
      scrollScrollBorder = false,
      ...props
    },
    ref
  ) => {
    const [showScrollBorder, setShowScrollBorder] = useState(false);

    // Use native scrolling for Safari horizontal scroll to avoid scroll blocking issues
    if (isSafari() && orientation === "horizontal") {
      return (
        <div
          ref={ref}
          className={classNames(
            "overflow-x-auto overflow-y-hidden",
            className,
            contentClassName
          )}
          {...props}
        >
          {children}
        </div>
      );
    }

    const handleOnScroll = throttle((e: React.UIEvent<HTMLDivElement>) => {
      if (!scrollScrollBorder || orientation !== "vertical" || !e.currentTarget)
        return;

      const { scrollTop } = e.currentTarget;
      const threshold = 1;

      setShowScrollBorder(scrollTop > threshold);
    }, 100);

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={classNames("relative", className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          className={classNames(
            "h-full w-full rounded-[inherit]",
            showScrollBorder ? "border-t border-border-base" : "",
            contentClassName
          )}
          onScroll={handleOnScroll}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar
          hideScrollBar={hideScrollBar}
          orientation={orientation}
          className={scrollBarClassName}
        />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  }
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.ScrollAreaScrollbar
  > & {
    hideScrollBar?: boolean;
  }
>(
  (
    { className, orientation = "vertical", hideScrollBar = false, ...props },
    ref
  ) => (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={classNames(
        "flex touch-none select-none transition-colors",
        orientation === "vertical" &&
          "h-full w-2.5 border-l border-l-transparent p-px",
        orientation === "horizontal" &&
          "h-2.5 flex-col border-b border-b-transparent p-px",
        className
      )}
      {...props}
    >
      {!hideScrollBar && (
        <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-bg-subtle" />
      )}
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
);
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
